// §4 S1 — Work order list, /production/work-orders (Module 04, slice 04-A).
//
// The filter state lives in the URL (validateSearch on the route), so a
// filtered list survives a reload and a share, and a saved view can name it.
// §4's "priority" filter is served as a SORT: §5 row 2 exposes
// `sort=priority` and no priority filter (spec §4 S1 amended under D-071).
//
// SAVED VIEWS (spec 04 §4 S1, spec 16 §3.12 and §4, D-305). The list adopts
// the shared views layer (`areas/office/views`). Its three chips, Overdue, In
// progress and Awaiting close, are the system views migration 0070 inserts,
// served by the same views read as the reader's own, and the URL presets and
// `matchesPreset` they replaced are gone, so the chips have one path. Every
// filter and sort write goes through `views.write`, where the order-token rule
// judges it. The list read waits on `views.listEnabled`, so an address naming
// a view alone is read once, with the view applied. The switcher, the filters
// and the table are one region, revealed together once the views read and the
// first page have settled (design-system §5). The peg pair is not a filter, so
// applying a view, un-pressing its chip and pressing All keep it (D-305 ruling
// 18). The sort select's absent value is By creation date, the order §5 row 2
// serves when `sort` is absent, so the select, the server and the view
// comparison name one default (ruling 17).
//
// COST-TO-DATE IS TWO-LAYERED (§7): the column is present only when the mirror
// grants `production.view_costs` AND the rows actually carry the accumulator
// keys — redaction is KEY ABSENCE, and an absent key is never summed as 0. For
// qc / maintenance / storekeeper / sales / floor_worker the column does not
// exist, it is not dashed. The two layers are the column's `presence` (D-305
// ruling 8), so a saved view that shows the column shows nothing to them and
// the column chooser does not list it.
//
// Sales reads its OWN BOOK (P3-H-3, D-391 ruling 1): the work orders pegged to
// lines of orders the rep owns or whose customer is assigned to them, which
// module 09 answers through its port. Until P3-H-3 the arm admitted nothing
// and this list was empty by construction. A rep cannot create a work order,
// so their empty state says which work orders this list holds for them and
// that each order's page lists its own, instead of "create one" (spec 04 §4
// S1's roles clause). A filtered-empty list reads as filtered for them too.
// The mirror decides who that reader is (`readsOwnBookOnly`), from role codes
// alone, and answers false wherever it cannot know.
//
// THE SALES ORDER COLUMN (P3-H-3, D-391 ruling 3) shows the pegged order's
// number, line and customer as module 09 labelled them for this reader, or
// "Sales order" when its customer door refuses the reader, and is blank for a
// work order made to stock (components/order-refs.tsx).
//
// THE PEG REQUEST (D-290): `peg_order_id` + `peg_line_id` — the sales order
// detail's "Plan a work order" link — open the create modal PRE-PEGGED to that
// line, for a `production.wo.write` holder on a desktop only (the same two
// gates as the New button: §4's mobile rule removes every state-changing
// control below `md`). Closing the modal takes the pair out of the URL, so a
// reload does not reopen it.
//
// THE SEARCH BOX debounces into the URL, and follows the URL back: applying a
// view, un-pressing it or All can change `q` under it, and a box still
// holding the old text would name a search the list is not running. Its
// `maxLength` is `VIEW_SEARCH_TEXT_MAX`, the cap the address validator and a
// saved view's `q` share (R7, PR-E4 wave 4).
//
// OFFLINE (R2). A list read paused for want of a network is revealed as the
// OfflineState the sales books show, never held behind the reserving skeleton,
// and a paused views read settles as a failed one in the controller, so the
// list opens from the address exactly as it does after a failed views read.
//
// THE LIST'S OWN WORDS (W8, PR-E4 wave 5). A saved view the server refuses
// for one of its stored filters names that filter as this bar does, in the
// tenant's terminology (`filterLabels`), rather than in the views layer's
// generic words. The status select says what a filter of several statuses
// holds, the statuses themselves as a list, instead of reading Any over a
// filter in force; choosing one status from it narrows to that one. A search
// holding U+0000, which no address or view can carry, empties the box at
// once, as the address validator drops it.

import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { PageHeader } from "../../../../components/breadcrumbs";
import { Button } from "../../../../components/button";
import { CheckboxField } from "../../../../components/field";
import { DataTable, type DataTableColumn } from "../../../../components/data-table";
import { FilterBar, type FilterChip } from "../../../../components/filter-bar";
import { SalesOrderRef } from "../components/order-refs";
import { EmptyState, ErrorState, OfflineState, TableSkeleton } from "../../../../components/states";
import { StatusChip } from "../../../../components/status-chip";
import { UnauthorizedError } from "../../../../lib/problem";
import { requestIdOf } from "../../../../lib/request";
import { useDebouncedValue } from "../../../../lib/use-debounced-value";
import { useTerm } from "../../../onboarding/hooks/terminology";
import {
  SAVED_VIEW_SCREENS_WEB,
  useSavedViews,
  VIEW_SEARCH_TEXT_MAX,
  ViewSwitcher,
  ViewsRegion,
} from "../../views";
import { CreateReleaseModal } from "../components/create-release-modal";
import { FacilityFilter } from "../components/facility-filter";
import { useIsDesktop } from "../hooks/use-is-desktop";
import {
  useFacilities,
  useProductIndex,
  useWorkCenters,
  useWorkOrders,
  type WorkOrder,
  type WorkOrdersQuery,
} from "../hooks/production-queries";
import { useProductionPermissions } from "../hooks/permissions";
import {
  costToDateMinor,
  formatBusinessDate,
  formatMinor,
  formatQty,
  isWorkOrderOverdue,
  localeLabel,
  parseQty,
  productionChipVariant,
} from "../lib/format";
import { validateWorkOrdersSearch, type WorkOrdersSearch } from "./search-params";
import { pickableWorkCenters } from "../../../../lib/work-centers";

const PAGE_SIZE = 50;

const WO_STATUSES = [
  "draft",
  "released",
  "in_progress",
  "completed",
  "closed",
  "cancelled",
] as const;

/** qty_good / qty_planned as a clamped 0–100 percent, on the scaled integers. */
function progressPct(wo: WorkOrder): number {
  const good = parseQty(wo.qty_good);
  const planned = parseQty(wo.qty_planned);
  if (good === undefined || planned === undefined || planned <= 0n) return 0;
  if (good <= 0n) return 0;
  if (good >= planned) return 100;
  return Number((good * 100n) / planned);
}

/** The sort the list serves when `sort` is absent (spec 04 §5 row 2), which the
 * views layer reads as the declared default (D-305 ruling 17). */
const DEFAULT_SORT = SAVED_VIEW_SCREENS_WEB["production.work_orders"].order.declaredDefault;

/** The status select's value while the filter holds several statuses: an
 * option that names them, never a status the reader could choose. */
const MULTI_STATUS = "__several__";

/** Every filter key cleared, from the one table the view layer reads, so a
 * filter the contract adds is cleared by Clear filters as well. */
const CLEARED_FILTERS = Object.fromEntries(
  SAVED_VIEW_SCREENS_WEB["production.work_orders"].filterKeys.map((key) => [key, undefined]),
) as Partial<WorkOrdersSearch>;

export function WorkOrdersListScreen() {
  const { t, i18n } = useTranslation();
  const { term } = useTerm();
  const navigate = useNavigate();
  const perms = useProductionPermissions();

  // Re-validated defensively: `strict: false` merges every route's search
  // types, and a shared link with a stale param must still open the screen.
  const rawSearch = useSearch({ strict: false });
  const search = useMemo(
    () => validateWorkOrdersSearch(rawSearch as Record<string, unknown>),
    [rawSearch],
  );

  // The raw address writer. Only the views controller calls it directly; the
  // screen's own writes go through `views.write` (below).
  const setSearch = (next: WorkOrdersSearch) => {
    void navigate({ to: "/production/work-orders", search: next, replace: true });
  };

  const baseQuery = useMemo<WorkOrdersQuery>(() => {
    const query: WorkOrdersQuery = { limit: PAGE_SIZE };
    if (search.status !== undefined) query.status = [...search.status];
    if (search.facility_id !== undefined) query.facility_id = search.facility_id;
    if (search.work_center_id !== undefined) query.work_center_id = search.work_center_id;
    if (search.product_id !== undefined) query.product_id = search.product_id;
    if (search.due_before !== undefined) query.due_before = search.due_before;
    if (search.due_after !== undefined) query.due_after = search.due_after;
    if (search.overdue === true) query.overdue = "true";
    if (search.q !== undefined) query.q = search.q;
    if (search.sort !== undefined) query.sort = search.sort;
    return query;
  }, [search]);

  // Cursor pagination as LOAD MORE: earlier pages are kept in state and the
  // live query serves the page at the current cursor; a filter change, and so
  // applying a view, resets.
  const filterKey = JSON.stringify(baseQuery);
  const [paged, setPaged] = useState<{
    key: string;
    cursor: string | undefined;
    rows: readonly WorkOrder[];
  }>({ key: filterKey, cursor: undefined, rows: [] });
  if (paged.key !== filterKey) {
    // Derived reset during render (the React-recommended pattern) — the stale
    // pages belong to a different filter set and must not flash.
    setPaged({ key: filterKey, cursor: undefined, rows: [] });
  }

  const query = useMemo<WorkOrdersQuery>(
    () => (paged.cursor !== undefined ? { ...baseQuery, cursor: paged.cursor } : baseQuery),
    [baseQuery, paged.cursor],
  );

  // THE LIST GATE (D-305 ruling 21). The views controller decides when the
  // list may be read, and it needs the rows that read returns, so the read
  // comes first and its `enabled` is the controller's verdict carried in
  // state. It starts closed, so the first render can never issue the bare
  // read an address naming a view alone must not send, and it is brought to
  // the controller's verdict in the same render, before anything commits.
  const [listGate, setListGate] = useState(false);
  const list = useWorkOrders(query, { enabled: listGate });
  // While the gate is closed the list shows nothing it may hold in the cache:
  // that page belongs to the address before the view applies, and the next
  // read replaces it.
  const page = listGate ? list.data : undefined;
  const listPending = !listGate || list.isPending;
  const listError = listGate ? list.error : null;
  // The read is paused for want of a network: the region reveals it as
  // offline rather than waiting on it (R2).
  const paused = listGate && list.fetchStatus === "paused";

  useEffect(() => {
    if (listError instanceof UnauthorizedError) void navigate({ to: "/login" });
  }, [listError, navigate]);

  const rows = useMemo<readonly WorkOrder[]>(
    () => [...paged.rows, ...(page?.items ?? [])],
    [paged.rows, page],
  );

  const facilities = useFacilities();
  const facilityName = useMemo(() => {
    const map = new Map<string, string>();
    for (const facility of facilities.data?.items ?? []) map.set(facility.id, facility.name);
    return map;
  }, [facilities.data]);

  const workCenters = useWorkCenters(search.facility_id);
  const products = useProductIndex(rows.map((row) => row.product_id));

  const canCreate = perms.has("production.wo.write");
  // The sales own-book reader: empty by construction, filters or none (the
  // header).
  const ownBookOnly = perms.readsOwnBookOnly;
  // §7 layer 1: the mirror. Layer 2 is the keys actually served, which the
  // cost column's `presence` reads from the loaded rows. Both must hold.
  const mirrorGrantsCosts = perms.has("production.view_costs");

  const filtered =
    search.status !== undefined ||
    search.facility_id !== undefined ||
    search.work_center_id !== undefined ||
    search.product_id !== undefined ||
    search.due_before !== undefined ||
    search.due_after !== undefined ||
    search.overdue === true ||
    search.q !== undefined;

  const [createOpen, setCreateOpen] = useState(false);
  const isDesktop = useIsDesktop();
  const pegRequest =
    search.peg_order_id !== undefined && search.peg_line_id !== undefined
      ? { orderId: search.peg_order_id, lineId: search.peg_line_id }
      : undefined;
  const peg = pegRequest !== undefined && canCreate && isDesktop ? pegRequest : undefined;

  const columns: DataTableColumn<WorkOrder>[] = [
    {
      key: "wo_no",
      header: t("production.list.col.number"),
      id: true,
      render: (wo) => (
        <Link to="/production/work-orders/$id" params={{ id: wo.id }}>
          {wo.wo_no}
        </Link>
      ),
    },
    {
      key: "product_id",
      header: term("product"),
      render: (wo) => {
        const product = products.byId.get(wo.product_id);
        return product !== undefined ? `${product.sku} · ${product.name}` : "";
      },
    },
    {
      key: "progress",
      header: t("production.list.col.progress"),
      render: (wo) => (
        <>
          <span className="progress-track" aria-hidden="true">
            <span className="progress-fill" style={{ width: `${String(progressPct(wo))}%` }} />
          </span>
          {t("production.list.progressOf", {
            good: formatQty(wo.qty_good, { locale: i18n.language }),
            planned: formatQty(wo.qty_planned, { locale: i18n.language }),
          })}
        </>
      ),
    },
    {
      key: "status",
      header: t("production.list.col.status"),
      render: (wo) => (
        <StatusChip variant={productionChipVariant(wo.status)} className="status-chip">
          {t(`production.status.wo.${wo.status}`)}
        </StatusChip>
      ),
    },
    {
      key: "due_date",
      header: t("production.list.col.due"),
      render: (wo) =>
        wo.due_date === null ? (
          t("production.common.notAvailable")
        ) : isWorkOrderOverdue(wo) ? (
          <span className="due-overdue" data-testid={`due-overdue-${wo.wo_no}`}>
            {formatBusinessDate(wo.due_date, i18n.language)}
          </span>
        ) : (
          formatBusinessDate(wo.due_date, i18n.language)
        ),
    },
    {
      key: "sales_order",
      header: t("production.list.col.salesOrder"),
      render: (wo) =>
        wo.sales_order_line !== undefined ? (
          <SalesOrderRef value={wo.sales_order_line} testId={`wo-sales-order-${wo.wo_no}`} />
        ) : (
          ""
        ),
    },
    {
      key: "facility_id",
      header: term("facility"),
      render: (wo) => facilityName.get(wo.facility_id) ?? "",
    },
    {
      key: "cost_to_date_minor",
      header: t("production.list.col.costToDate"),
      numeric: true,
      // §7 redaction-by-absence, both layers (D-305 ruling 8): the mirror
      // grants `production.view_costs` AND a loaded row carries the four
      // accumulators. Never `key in row`: the column is derived, and a key
      // alone is not the grant. Without both there is no column, no dash and
      // no chooser entry, whatever a view says.
      presence: (loaded) =>
        mirrorGrantsCosts && loaded.some((wo) => costToDateMinor(wo) !== undefined),
      render: (wo) => {
        const cost = costToDateMinor(wo);
        return cost !== undefined ? formatMinor(cost, wo.currency, i18n.language) : "";
      },
    },
  ];

  const views = useSavedViews<WorkOrdersSearch, WorkOrder>({
    screenKey: "production.work_orders",
    search,
    setSearch,
    validate: validateWorkOrdersSearch,
    columns,
    rows,
    rowsLoading: listPending || list.isFetching,
    // The bar's own labels, the tenant's terminology included (W8).
    filterLabels: {
      status: t("production.list.filter.status"),
      facility_id: term("facility"),
      work_center_id: term("work_center"),
      product_id: term("product"),
      due_after: t("production.list.filter.from"),
      due_before: t("production.list.filter.to"),
      overdue: t("production.list.filter.overdueOnly"),
      q: t("production.list.filter.search"),
    },
  });
  if (listGate !== views.listEnabled) setListGate(views.listEnabled);

  // THE SEARCH BOX. It debounces into the URL rather than firing a request
  // per key, and follows the URL when something else changes `q` (a view
  // applied or cleared). `writtenQ` is the value this box last wrote, so its
  // own write landing is not mistaken for a change from elsewhere and the
  // characters typed since are kept.
  const addressQ = search.q ?? "";
  const [qInput, setQInput] = useState(addressQ);
  const writtenQ = useRef<string | undefined>(undefined);
  const debouncedQ = useDebouncedValue(qInput, 300);
  useEffect(() => {
    const next = debouncedQ.trim();
    if (next === addressQ) return;
    writtenQ.current = next;
    views.write({ ...search, q: next.length > 0 ? next : undefined });
    // Deps: the debounced value only — this effect is a URL WRITE, and keying
    // it on `search` would re-fire it on its own navigation.
  }, [debouncedQ]);
  useEffect(() => {
    if (writtenQ.current === addressQ) {
      writtenQ.current = undefined;
      return;
    }
    setQInput((typed) => (typed.trim() === addressQ ? typed : addressQ));
  }, [addressQ]);

  const patchSearch = (patch: Partial<WorkOrdersSearch>) => {
    views.write({ ...search, ...patch });
  };

  // A filter of several statuses (a pasted link or a saved view) is shown as
  // the statuses it holds, one option the select stands on (W8).
  const statuses = search.status ?? [];
  const statusSelectValue =
    statuses.length === 0 ? "" : statuses.length === 1 ? (statuses[0] ?? "") : MULTI_STATUS;
  const multiStatusLabel =
    statuses.length > 1
      ? new Intl.ListFormat(i18n.language, { type: "conjunction", style: "narrow" }).format(
          statuses.map((status) => t(`production.status.wo.${status}`)),
        )
      : undefined;

  // ONE FILTER ROW (rev-7 FilterBar, D-426). Nine controls used to stack
  // above the first work order on a phone. The row keeps the search box, with
  // Status and the facility beside it from 769 px up; the work centre, the due
  // dates, Overdue only and the sort sit behind Filters, and on a phone every
  // control does. A filter the reader cannot see in the row is a chip under
  // it, named in words: a facility by its name, a date as a date.
  const statusControl = (
    <label className="filter-chip">
      <span className="filter-chip-label">{t("production.list.filter.status")}</span>
      <select
        value={statusSelectValue}
        onChange={(event) => {
          const value = event.target.value;
          if (value === MULTI_STATUS) return;
          patchSearch({
            status: value.length > 0 ? [value as (typeof WO_STATUSES)[number]] : undefined,
          });
        }}
      >
        <option value="">{t("production.list.filter.any")}</option>
        {multiStatusLabel !== undefined ? (
          <option value={MULTI_STATUS}>{multiStatusLabel}</option>
        ) : null}
        {WO_STATUSES.map((status) => (
          <option key={status} value={status}>
            {t(`production.status.wo.${status}`)}
          </option>
        ))}
      </select>
    </label>
  );
  const facilityControl = (
    <FacilityFilter
      value={search.facility_id ?? ""}
      allowAll
      facilities={facilities.data?.items ?? []}
      onChange={(value) => {
        patchSearch({
          facility_id: value.length > 0 ? value : undefined,
          // A work centre belongs to its facility; the narrower filter
          // cannot outlive the wider one.
          work_center_id: undefined,
        });
      }}
    />
  );
  const activeWorkCenters = pickableWorkCenters(workCenters.data?.items);
  const workCenterControl =
    search.facility_id !== undefined ? (
      <label className="filter-chip">
        <span className="filter-chip-label">{term("work_center")}</span>
        <select
          value={search.work_center_id ?? ""}
          onChange={(event) => {
            const value = event.target.value;
            patchSearch({ work_center_id: value.length > 0 ? value : undefined });
          }}
        >
          <option value="">{t("production.list.filter.any")}</option>
          {activeWorkCenters.map((wc) => (
            <option key={wc.id} value={wc.id}>
              {localeLabel(wc.name_i18n, i18n.language)}
            </option>
          ))}
        </select>
      </label>
    ) : null;
  const dateControls = (
    <>
      <label className="filter-chip">
        <span className="filter-chip-label">{t("production.list.filter.from")}</span>
        <input
          type="date"
          value={search.due_after ?? ""}
          onChange={(event) => {
            const value = event.target.value;
            patchSearch({ due_after: value.length > 0 ? value : undefined });
          }}
        />
      </label>
      <label className="filter-chip">
        <span className="filter-chip-label">{t("production.list.filter.to")}</span>
        <input
          type="date"
          value={search.due_before ?? ""}
          onChange={(event) => {
            const value = event.target.value;
            patchSearch({ due_before: value.length > 0 ? value : undefined });
          }}
        />
      </label>
      {/* A checkbox is a ROW, not a stacked field: inside a bare
          `.filter-chip` the box stretched to the chip's width (F6). */}
      <CheckboxField
        label={t("production.list.filter.overdueOnly")}
        checked={search.overdue === true}
        onChange={(checked) => {
          patchSearch({ overdue: checked ? true : undefined });
        }}
      />
    </>
  );
  const sortControl = (
    <label className="filter-chip">
      <span className="filter-chip-label">{t("production.list.filter.sort")}</span>
      <select
        // An absent `sort` is the order the server serves, By creation
        // date (§5 row 2, D-305 ruling 17), and choosing it writes the
        // default as an absent key.
        value={search.sort ?? DEFAULT_SORT}
        onChange={(event) => {
          const value = event.target.value as NonNullable<WorkOrdersSearch["sort"]>;
          patchSearch({ sort: value === DEFAULT_SORT ? undefined : value });
        }}
      >
        <option value="due_date">{t("production.list.filter.sortDue")}</option>
        <option value="priority">{t("production.list.filter.sortPriority")}</option>
        <option value="created_at">{t("production.list.filter.sortCreated")}</option>
      </select>
    </label>
  );

  const filterChips: FilterChip[] = [];
  const addChip = (key: string, label: string, clear: Partial<WorkOrdersSearch>, inRow = false) => {
    // A filter in the row shows its own value from 769 px up.
    if (inRow && isDesktop) return;
    filterChips.push({
      key,
      label,
      onRemove: () => {
        patchSearch(clear);
      },
    });
  };
  if (statuses.length > 0) {
    const value = multiStatusLabel ?? t(`production.status.wo.${statuses[0] ?? ""}`);
    addChip(
      "status",
      `${t("production.list.filter.status")}: ${value}`,
      { status: undefined },
      true,
    );
  }
  if (search.facility_id !== undefined) {
    const name = facilities.data?.items.find((f) => f.id === search.facility_id)?.name;
    addChip(
      "facility",
      name === undefined ? term("facility") : `${term("facility")}: ${name}`,
      { facility_id: undefined, work_center_id: undefined },
      true,
    );
  }
  if (search.work_center_id !== undefined) {
    const wc = activeWorkCenters.find((w) => w.id === search.work_center_id);
    addChip(
      "work_center",
      wc === undefined
        ? term("work_center")
        : `${term("work_center")}: ${localeLabel(wc.name_i18n, i18n.language)}`,
      { work_center_id: undefined },
    );
  }
  if (search.due_after !== undefined) {
    addChip(
      "due_after",
      `${t("production.list.filter.from")}: ${formatBusinessDate(search.due_after, i18n.language)}`,
      { due_after: undefined },
    );
  }
  if (search.due_before !== undefined) {
    addChip(
      "due_before",
      `${t("production.list.filter.to")}: ${formatBusinessDate(search.due_before, i18n.language)}`,
      { due_before: undefined },
    );
  }
  if (search.overdue === true) {
    addChip("overdue", t("production.list.filter.overdueOnly"), { overdue: undefined });
  }

  return (
    <section className="wo-list">
      <PageHeader
        title={term("work_order")}
        actions={
          canCreate ? (
            // Inside .wo-transitions: §4's mobile rule removes every
            // state-changing control below `md` (production.css).
            <span className="wo-transitions">
              <Button
                variant="primary"
                onClick={() => {
                  setCreateOpen(true);
                }}
              >
                {t("production.list.createButton")}
              </Button>
            </span>
          ) : undefined
        }
      />

      {createOpen || peg !== undefined ? (
        <CreateReleaseModal
          // A different line is a different form: never carry one line's
          // typed quantity into another's.
          key={peg === undefined ? "create" : `${peg.orderId}:${peg.lineId}`}
          peg={peg}
          onClose={() => {
            setCreateOpen(false);
            if (pegRequest !== undefined) {
              patchSearch({ peg_order_id: undefined, peg_line_id: undefined });
            }
            // The create/release invalidations refetch page 1; drop the
            // accumulated pages so the fresh order is not buried below stale
            // ones.
            setPaged({ key: filterKey, cursor: undefined, rows: [] });
          }}
        />
      ) : null}

      {/* One content region (spec 16 §4 Loading): the chip row's height is
          decided by the views read, so the switcher, the filters and the
          table are revealed together once the views read and the first page
          have settled, and the reveal latches. */}
      <ViewsRegion ready={views.settled && (!listPending || paused)} rows={6} cols={6}>
        <ViewSwitcher views={views} />

        <div className="wo-filter-row">
          <FilterBar
            data-testid="wo-filters"
            search={{
              value: qInput,
              label: t("production.list.filter.search"),
              placeholder: t("production.list.filter.searchPlaceholder"),
              maxLength: VIEW_SEARCH_TEXT_MAX,
              // Text holding U+0000 can live in no address and no view, so
              // the box empties as the address drops it (W8).
              onChange: (value) => {
                setQInput(value.includes("\u0000") ? "" : value);
              },
            }}
            filters={{
              inline: (
                <>
                  {statusControl}
                  {facilityControl}
                </>
              ),
              more: (
                <>
                  {workCenterControl}
                  {dateControls}
                  {sortControl}
                </>
              ),
              // "Filters (2)" counts what the row does not already show.
              label:
                filterChips.length > 0
                  ? t("production.list.filter.filtersCount", { n: filterChips.length })
                  : t("production.list.filter.filters"),
              title: t("production.list.filter.filters"),
              doneLabel: t("production.list.filter.done"),
            }}
            chips={filterChips}
            removeLabel={(chip) => t("production.list.filter.remove", { label: chip.label })}
            clearAll={{
              label: t("production.list.filter.clear"),
              // The filters only: the active view, the order and the peg
              // pair are not filters, and the order-token rule decides what
              // the address says about the view (spec 16 §3.12 item 5).
              onClear: () => {
                setQInput("");
                patchSearch(CLEARED_FILTERS);
              },
            }}
          />
        </div>

        {paused && rows.length === 0 ? (
          <OfflineState
            title={t("production.list.offlineTitle")}
            body={t("production.list.offlineBody")}
          />
        ) : null}

        {listPending && !paused ? (
          <TableSkeleton rows={6} cols={6} label={t("production.common.loading")} />
        ) : null}

        {listError && !(listError instanceof UnauthorizedError) ? (
          <ErrorState
            title={t("production.list.error")}
            detail={listError.message}
            retryLabel={t("production.common.retry")}
            onRetry={() => void list.refetch()}
            requestId={requestIdOf(listError)}
            requestIdLabel={t("production.common.requestId")}
          />
        ) : null}

        {page !== undefined && rows.length === 0 ? (
          <EmptyState
            title={
              filtered
                ? t("production.list.emptyFilteredTitle")
                : ownBookOnly
                  ? t("production.list.emptyTitleOwnBook")
                  : t("production.list.emptyTitle")
            }
            body={
              filtered
                ? t("production.list.emptyFilteredBody")
                : ownBookOnly
                  ? t("production.list.emptyBodyOwnBook")
                  : canCreate
                    ? t("production.list.emptyBody")
                    : t("production.list.emptyBodyReadOnly")
            }
            {...(!filtered && canCreate && isDesktop
              ? {
                  ctaLabel: t("production.list.createButton"),
                  onCta: () => {
                    setCreateOpen(true);
                  },
                }
              : {})}
          />
        ) : null}

        {rows.length > 0 ? (
          <>
            <DataTable
              columns={columns}
              rows={rows}
              rowKey={(wo) => wo.id}
              empty={null}
              rowProps={(wo) => ({ "data-testid": `wo-row-${wo.wo_no}` })}
              columnConfig={views.columnConfig}
            />
            {page !== undefined && page.next_cursor !== null ? (
              <Button
                onClick={() => {
                  if (page.next_cursor === null) return;
                  setPaged((prev) => ({
                    key: prev.key,
                    cursor: page.next_cursor ?? undefined,
                    rows: [...prev.rows, ...page.items],
                  }));
                }}
              >
                {t("production.list.loadMore")}
              </Button>
            ) : null}
          </>
        ) : null}
      </ViewsRegion>
    </section>
  );
}
